import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOffsetRegistryStore } from "../store/offsetRegistryStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * Smart Offset Registry (v2)
 * 
 * UI for managing offset balances, local/imported offsets,
 * credit aging, and fraud risk calculations.
 */
export default function SmartOffsetV2() {
  const { t } = useTranslation();
  const {
    offsetBalance,
    localOffsets,
    importedOffsets,
    creditAgeDays,
    setOffsetBalance,
    setLocalOffsets,
    setImportedOffsets,
    setCreditAgeDays,
    exportOffsetBalance,
  } = useOffsetRegistryStore();

  // Loading and error states
  const [error, setError] = useState<Error | null>(null);

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("Smart Offset");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("offsetBalance", offsetBalance);
    useAIContext.getState().setInput("localOffsets", localOffsets);
    useAIContext.getState().setInput("importedOffsets", importedOffsets);
    useAIContext.getState().setInput("creditAgeDays", creditAgeDays);
  }, [offsetBalance, localOffsets, importedOffsets, creditAgeDays]);

  const fraudRisk = useOffsetRegistryStore((s) => s.getFraudRisk());
  const localBoost = useOffsetRegistryStore((s) => s.getLocalBoost());
  const agingBuckets = useOffsetRegistryStore((s) => s.getAgingBuckets());

  const handleExport = () => {
    setError(null);
    try {
      exportOffsetBalance();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("offsetRegistry.exportFailed") || "Failed to export offset balance");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100 p-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("offsetRegistry.title") || "Smart Offset Registry"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("offsetRegistry.offsetBalance") || "Offset Balance"}
          </label>
          <input
            type="number"
            min="0"
            value={offsetBalance}
            onChange={(e) => setOffsetBalance(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("offsetRegistry.localOffsets") || "Local Offsets"}
          </label>
          <input
            type="number"
            min="0"
            value={localOffsets}
            onChange={(e) => setLocalOffsets(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("offsetRegistry.importedOffsets") || "Imported Offsets"}
          </label>
          <input
            type="number"
            min="0"
            value={importedOffsets}
            onChange={(e) => setImportedOffsets(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("offsetRegistry.creditAgeDays") || "Credit Age Days (comma-separated)"}
          </label>
          <input
            type="text"
            value={creditAgeDays.join(", ")}
            onChange={(e) => {
              const ages = e.target.value
                .split(",")
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => !isNaN(n));
              setCreditAgeDays(ages);
            }}
            placeholder="30, 45, 120, 400"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Derived Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("offsetRegistry.fraudRisk") || "Fraud Risk"}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {fraudRisk.toFixed(2)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("offsetRegistry.localBoost") || "Local Boost"}
          </div>
          <div className="text-lg font-orbitron text-green-400">
            +{localBoost.toFixed(2)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50 col-span-2">
          <div className="text-xs text-neutral-400 mb-2">
            {t("offsetRegistry.agingBuckets") || "Aging Buckets"}
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            {Object.entries(agingBuckets).map(([bucket, count]) => (
              <div key={bucket} className="text-center">
                <div className="text-evat-neon">{count}</div>
                <div className="text-neutral-500">{bucket}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("offsetRegistry.export") || "Export JSON report"}
        >
          {t("offsetRegistry.export") || "Export JSON"}
        </button>
      </div>
    </div>
  );
}




